import {
    Show,
    Edit,
    SimpleForm,
    TextField,
    TextInput,
    required,
    maxLength,
    RichTextField,
    usePermissions,
    FunctionField,
    useRecordContext,
    TopToolbar,
    BooleanField,
    BooleanInput,
    useTranslate,
} from 'react-admin';
import PropTypes from 'prop-types';
import CustomList from '@/components/global/list/CustomList.jsx';
import { CustomDatagridConfigurable } from '@/components/global/list/CustomDatagridConfigurable.jsx';
import CustomCreate from '@/components/global/list/CustomCreate.jsx';
import { noCharactersFromList } from '@/components/global/validators/customValidators.jsx';
import { EditToolbar } from '@/components/global/buttons/toolbar.jsx';
import CustomMorePopover from '@/components/global/buttons/CustomMorePopover';
import CustomShowModalButton from '@/components/global/buttons/CustomShowModalButton.jsx';
import CustomEditModalButton from '@/components/global/buttons/CustomEditModalButton.jsx';
import { CustomSelectColumnsButton } from '@/components/global/buttons/CustomSelectColumnsButton.jsx';
import CustomToolbarActionsPopover from '@/components/global/buttons/CustomToolbarActionsPopover';
import CustomCreateButton from '@/components/global/buttons/CustomCreateButton.jsx';
import CustomExportButton from '@/components/global/buttons/CustomExportButton.jsx';
import ColumnsIcon from '@/assets/icons/columns-icon.svg?react';
import { useDrawer } from '@/composables/useDrawer';
import { RightDrawer } from '@/components/global/drawers/RightDrawer';
import { ChangeHistory } from '@/components/global/history/ChangeHistory.jsx';
import { SearchFilterInput } from '@/components/global/filters/SearchFilterInput.jsx';
import { useTheme, useMediaQuery } from '@mui/material';
import { useMemo } from 'react';
import CustomFilterButton from '@/components/global/filters/CustomFilterButton';
import { CustomRichText } from '@/components/global/inputs/CustomRichText';
import { DetailsSampleList } from '@/components/global/fields/DetailsSampleList.jsx';

const getFilters = (isMobile, translate) => [<TextInput key='filter_name' source='name_like' label={translate('common.name')} alwaysOn={isMobile} />];

const MoreActionsPopover = ({ permissions }) => {
    const record = useRecordContext();
    const { openDrawer } = useDrawer();
    if (!record) return null;

    return (
        <>
            <CustomMorePopover permissions={permissions} record={record}>
                <CustomShowModalButton onOpen={() => openDrawer(record.id, 'service-company-show')} />
                {permissions.isAdmin && <CustomEditModalButton onOpen={() => openDrawer(record.id, 'service-company-edit')} />}
            </CustomMorePopover>
        </>
    );
};

const ListActions = () => {
    const { isLoading, permissions } = usePermissions();
    const translate = useTranslate();
    if (isLoading) return null;

    return (
        <TopToolbar>
            <SearchFilterInput />
            <CustomFilterButton />
            <CustomSelectColumnsButton startIcon={<ColumnsIcon />} />
            {(permissions.isAdmin || permissions.isServiceMaster) && (
                <CustomToolbarActionsPopover>
                    <CustomCreateButton label={translate('common.newServiceCompany')} />
                    <CustomExportButton />
                </CustomToolbarActionsPopover>
            )}
        </TopToolbar>
    );
};

export const ServiceCompanyList = () => {
    const translate = useTranslate();
    const { isLoading, permissions } = usePermissions();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const filters = useMemo(() => getFilters(isMobile, translate), [isMobile, translate]);
    if (isLoading) return null;

    return (
        <CustomList filters={filters} actions={<ListActions />}>
            <CustomDatagridConfigurable bulkActionButtons={false} rowClick={false}>
                <TextField source='id' label={translate('resources.contractor.fields.id')} />
                <TextField source='name' label={translate('common.name')} />
                <BooleanField source='isDefault' label={translate('resources.serviceCompany.labels.isDefault')} />
                <BooleanField source='isRequired' label={translate('resources.serviceCompany.labels.isRequired')} />
                <FunctionField label={translate('common.options')} render={record => <MoreActionsPopover permissions={permissions} groupId={record.id} />} />
            </CustomDatagridConfigurable>
        </CustomList>
    );
};

export const ServiceCompanyCreate = ({ handleClose }) => {
    const translate = useTranslate();
    return (
        <RightDrawer handleClose={handleClose} title={translate('common.newServiceCompany')}>
            <CustomCreate handleClose={handleClose}>
                <ServiceCompanyForm />
            </CustomCreate>
        </RightDrawer>
    );
};

export const ServiceCompanyShow = ({ handleClose, elementId }) => {
    const translate = useTranslate();
    return (
        <RightDrawer
            handleClose={handleClose}
            title={translate('common.serviceCompanyDetails')}
            after={<ChangeHistory resource='service-company' recordId={elementId} fieldsSource='resources.serviceCompany.labels' />}
        >
            <Show
                id={elementId}
                resource='service-company'
                actions={false}
                toolbar={false}
                redirect={resource => {
                    handleClose();
                    return resource;
                }}
            >
                <DetailsSampleList>
                    <TextField source='id' label={translate('resources.contractor.fields.id')} />
                    <TextField source='name' label={translate('common.name')} />
                    <RichTextField source='description' label={translate('common.description')} />
                    <BooleanField source='isDefault' label={translate('resources.serviceCompany.labels.isDefault')} />
                    <BooleanField source='isRequired' label={translate('resources.serviceCompany.labels.isRequired')} />
                </DetailsSampleList>
            </Show>
        </RightDrawer>
    );
};

ServiceCompanyShow.propTypes = {
    handleClose: PropTypes.func.isRequired,
    elementId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export const ServiceCompanyEdit = ({ handleClose, elementId }) => {
    const translate = useTranslate();
    return (
        <RightDrawer handleClose={handleClose} title={translate('common.serviceCompanyEdit')}>
            <Edit
                mutationMode='pessimistic'
                id={elementId}
                actions={false}
                redirect={resource => {
                    handleClose();
                    return resource;
                }}
            >
                <ServiceCompanyForm />
            </Edit>
        </RightDrawer>
    );
};

const ServiceCompanyForm = () => {
    const translate = useTranslate();
    return (
        <SimpleForm
            toolbar={<EditToolbar />}
            sx={{
                '& .MuiFormHelperText-root': {
                    display: 'none',
                },
                '& .rich-text-input': {
                    marginTop: '8px',
                    '& .tiptap': {
                        maxWidth: '100%',
                    },
                },
            }}
        >
            <TextInput
                source='name'
                label={translate('common.name')}
                validate={[
                    required(translate('common.validation.required')),
                    maxLength(150, translate('common.validation.maxLength', { count: 150 })),
                    noCharactersFromList('!@#$%^&*+/\\”"\'`|<>,.[]{}'),
                ]}
                fullWidth
            />
            <CustomRichText source='description' label={translate('common.description')} fullWidth />
            <BooleanInput source='isDefault' label={translate('resources.serviceCompany.labels.isDefault')} />
            <BooleanInput source='isRequired' label={translate('resources.serviceCompany.labels.isRequired')} />
        </SimpleForm>
    );
};
